import { BaseElementController } from '../../modules/BaseElementController';
import { MainMenuController } from '../MainMenu/MainMenuController';
import { LanguageController } from '../LanguageButton/LanguageController';
import { Header } from './Header';
import { HeaderStructure } from '../../data/types';
import { Config } from '../../modules/Config';
import { Translator } from '../../modules/translate';
import { DataFetcher } from '../../modules/DataFetcher';
import { SignInController } from '../SignIn/SignInController';
import { UserMenuController } from '../UserMenu/UserMenuController';
import { OverlayController } from '../Overlay/OverlayController';
import { MenuToggleController } from '../MenuToggle/MenuToggleController';
import { BiLogger } from '../../modules/BiLogger';
import * as s from './Header.scss';
import { Experiments } from '../../experiments';

interface HeaderControllerParams {
  headerStructure: HeaderStructure;
  config: Config;
  t: Translator;
  dataFetcher: DataFetcher;
  biLogger: BiLogger;
  experiments: Experiments;
}

export class HeaderController extends BaseElementController {
  private readonly headerStructure: HeaderStructure;
  private readonly config: Config;
  private readonly t: Translator;
  private readonly mainMenuController: MainMenuController;
  private readonly languageController: LanguageController;
  private readonly signInController: SignInController;
  private userMenuController?: UserMenuController;
  private readonly overlayController: OverlayController;
  private readonly menuToggleController: MenuToggleController;
  private readonly dataFetcher: DataFetcher;
  private readonly biLogger: BiLogger;
  private readonly experiments: Experiments;
  private readonly elements: { header: HTMLHeadingElement; container: HTMLDivElement };

  constructor({ headerStructure, config, t, dataFetcher, biLogger, experiments }: HeaderControllerParams) {
    super();
    let headerElement = this.$<HTMLHeadingElement>('[data-wix-header=hf2021]', document);
    if (!headerElement) {
      headerElement = this.createElementFromHTML<HTMLHeadingElement>(Header({ t, structure: headerStructure, config }));
      const headerWrapper = config.getHeaderWrapper();
      if (headerWrapper) {
        headerWrapper.replaceWith(headerElement);
      } else {
        document.body.insertBefore(headerElement, document.body.firstChild);
      }
    }

    this.elements = {
      header: headerElement,
      container: this.$<HTMLDivElement>(`.${s.header}`, headerElement)!,
    };

    this.headerStructure = headerStructure;
    this.config = config;
    this.t = t;
    this.biLogger = biLogger;
    this.experiments = experiments;

    this.mainMenuController = new MainMenuController({ biLogger, config });
    this.languageController = new LanguageController({
      languageList: this.headerStructure.languagePickerMenu,
      config,
      t,
      biLogger,
      headerElement,
    });
    this.signInController = new SignInController({ biLogger });
    this.overlayController = new OverlayController();
    this.menuToggleController = new MenuToggleController();
    this.dataFetcher = dataFetcher;
  }

  init() {
    if (this.experiments['specs.funnel.MoveEnterpriseLinkToTopMenu'] === 'true') {
      this.moveEnterpriseLinkToTopLevel();
    }

    this.renderUserProfileInfo();

    this.signInController.init();

    this.languageController.init({
      onLanguageButtonClick: () => {
        this.shadowForSignInContainer();
        this.mainMenuController.collapseOpenedMenu();
      },
    });

    this.mainMenuController.init({
      onClickMenuItem: () => {
        if (this.userMenuController?.isExpanded()) {
          this.userMenuController?.collapseMenu();
        }
        if (!this.isDesktop() && this.languageController.isOpenedLanguageMobileMenu()) {
          this.languageController.closeLanguageMobileMenu();
        }
        this.shadowForSignInContainer();
      },
    });

    this.menuToggleController.init({
      onOpen: () => {
        this.overlayController.show();
        this.doHeaderInFrontOfCookieBanner();
      },
      onHide: () => {
        this.overlayController.hide();
        this.doCookieBannerInFrontOfHeader();
      },
    });

    this.overlayController.init({ onClick: this.handleOverlayClick });

    if (this.config.isSticky()) {
      this.doHeaderSticky();
    }

    if (!this.config.getHeaderWrapper()) {
      this.moveHeaderToTop();
    }

    if (this.config.isSupportSite()) {
      this.doHeaderWithoutHeight();
    }

    this.handleOutsideClick();
    this.handleKeyboardEvents();
    this.disableFocusForMouseEvents();
  }

  private handleOutsideClick = () => {
    document.addEventListener('mousedown', (event) => {
      const { header } = this.elements;
      if (!header.contains(event.target as Node)) {
        this.mainMenuController.collapseOpenedMenu();
        this.userMenuController?.collapseMenu();
      }
    });
  };

  private renderUserProfileInfo() {
    this.dataFetcher
      .fetchUserProfileData()
      .then(async (userProfileData) => {
        if (userProfileData) {
          document.body.classList.add('logged_in');
          this.renderMySitesButton();
          this.signInController.removeElement();
          const userImage = await this.dataFetcher.fetchUserProfileImage();
          this.userMenuController = new UserMenuController({
            biLogger: this.biLogger,
            config: this.config,
            userProfileData,
            userImage,
            t: this.t,
            userMenuStructure: this.headerStructure.userMenu,
          });
          const { desktop, mobile } = this.userMenuController.renderUserMenu({
            onOpen: () => this.mainMenuController.collapseOpenedMenu(),
            onLanguageButtonClick: () =>
              this.languageController.openLanguagePopup(() => this.userMenuController?.getLanguageButtonElement().focus()),
          });

          this.mainMenuController.pushItem(desktop);
          this.mainMenuController.unshiftElement(mobile);
        }
      })
      .catch();
  }

  private renderMySitesButton() {
    const mySiteMenuItem = this.mainMenuController.createMenuItem({ item: this.headerStructure.mySites, t: this.t });
    this.mainMenuController.unshiftElement(mySiteMenuItem.getMainElement());
    mySiteMenuItem.initListeners({ onClickItemWithSubMenu: this.mainMenuController.onClickItemWithSubMenu });
  }

  private handleOverlayClick = (e: MouseEvent) => {
    if (e.target === this.overlayController.element) {
      this.mainMenuController.collapseOpenedMenu();
      this.userMenuController?.collapseMenu();
      this.menuToggleController.closeMenu();
      this.overlayController.hide();
    }
  };

  shadowForSignInContainer = () => {
    if (!this.isDesktop()) {
      setTimeout(() => {
        if (this.mainMenuController.isMenuHasScrollBar()) {
          this.signInController.addShadow();
        } else {
          this.signInController.removeShadow();
        }
      }, 50);
    }
  };

  disableFocusForMouseEvents() {
    const { header } = this.elements;
    window.addEventListener('keydown', this.handleFirstTab);
    window.addEventListener('mousedown', () => {
      setTimeout(() => {
        if (header.contains(document.activeElement)) {
          // @ts-ignore
          document.activeElement.blur();
        }
      }, 0);
    });
  }

  handleFirstTab = (e: KeyboardEvent) => {
    if (e.keyCode === 9) {
      document.body.classList.add('focused');

      window.removeEventListener('keydown', this.handleFirstTab);
      window.addEventListener('mousedown', this.handleMouseDownOnce);
    }
  };

  handleMouseDownOnce = () => {
    document.body.classList.remove('focused');

    window.removeEventListener('mousedown', this.handleMouseDownOnce);
    window.addEventListener('keydown', this.handleFirstTab);
  };

  handleKeyboardEvents = () => {
    window.addEventListener('keyup', this.handleEscapeButton);
  };

  handleEscapeButton = (e: KeyboardEvent) => {
    const isEscapePressed = e.key === 'Escape' || e.key === 'Esc';
    if (isEscapePressed) {
      if (this.userMenuController?.isExpanded() && !this.languageController.isOpened()) {
        this.userMenuController?.collapseMenu();
        this.userMenuController?.getToggleButtonElement().focus();
      }

      if (this.languageController.isOpened()) {
        this.languageController.closePopup();
      }
    }
  };

  doHeaderSticky() {
    this.elements.container.classList.add(s.fixed);
  }

  moveHeaderToTop() {
    document.body.insertBefore(this.elements.header, document.body.firstChild);
  }

  doHeaderWithoutHeight() {
    this.elements.header.classList.add(s.zeroHeight);
  }

  // we can not change default z-index because it can break design in tempates page
  doHeaderInFrontOfCookieBanner() {
    this.elements.header.classList.add(s.inFrontOfCookieBanner);
  }

  doCookieBannerInFrontOfHeader() {
    this.elements.header.classList.remove(s.inFrontOfCookieBanner);
  }

  moveEnterpriseLinkToTopLevel() {
    const oldEnterpriseEl = this.$('[data-bi-name=wix_for_enterprise]');
    const pricingMainMenuItem = this.mainMenuController.getMainMenuItem('pricing');
    if (pricingMainMenuItem && oldEnterpriseEl && oldEnterpriseEl.parentElement) {
      oldEnterpriseEl.parentElement.remove();

      const newEnterpriseItem = this.mainMenuController.createMenuItem({
        t: this.t,
        item: {
          text: 'sub_menu_wix_for_enterprise_v2',
          href: 'https://www.wix.com/enterprise',
          name: 'wix_for_enterprise',
        },
      });
      this.mainMenuController.getMenuElement().insertBefore(newEnterpriseItem.getMainElement(), pricingMainMenuItem.getMainElement());
      newEnterpriseItem.initListeners({ onClickItemWithSubMenu: this.mainMenuController.onClickItemWithSubMenu });
    }
  }
}
